import { isGatewayAuthFailure } from '../session/isGatewayAuthFailure';

/**
 * withAuthFailureInterceptor — wraps a gateway client so that auth failures
 * (401 / expired token) invoke onAuthFailure before re-throwing the error.
 *
 * This provides uniform recovery handling across gateway API consumers without
 * requiring per-page error handling. The wrapped client is a plain object with
 * the same contract as the original; known gateway methods are intercepted and
 * unknown properties are preserved.
 *
 * @param {object|null} client      - Gateway search client (may be null)
 * @param {Function}    onAuthFailure - Invoked with the error when auth failure detected
 * @returns {object|null} Wrapped client, or the original client/null when interception
 *                        is not possible.
 */
export function withAuthFailureInterceptor(client, onAuthFailure) {
  if (!client || typeof onAuthFailure !== 'function') return client;

  const wrap = (fn) => async (...args) => {
    try {
      const result = await fn(...args);
      return result;
    } catch (error) {
      if (isGatewayAuthFailure(error)) {
        onAuthFailure(error);
      }
      throw error;
    }
  };

  const wrapIfPresent = (methodName) => (
    typeof client[methodName] === 'function'
      ? wrap(client[methodName].bind(client))
      : client[methodName]
  );

  return {
    ...client,
    loadSearchExperienceDefaults: wrapIfPresent('loadSearchExperienceDefaults'),
    loadSearchExperienceConfig: wrapIfPresent('loadSearchExperienceConfig'),
    saveSearchExperienceConfig: wrapIfPresent('saveSearchExperienceConfig'),
    describeSearchExperienceConfig: wrapIfPresent('describeSearchExperienceConfig'),
    getSearchComponents: wrapIfPresent('getSearchComponents'),
    applyAgentUiPreview: wrapIfPresent('applyAgentUiPreview'),
    verifyAgentUiPreview: wrapIfPresent('verifyAgentUiPreview'),
    buildPreviewBundle: wrapIfPresent('buildPreviewBundle'),
    promoteBundle: wrapIfPresent('promoteBundle'),
    buildAndPromoteBundle: wrapIfPresent('buildAndPromoteBundle'),
    pollJobStatus: wrapIfPresent('pollJobStatus'),
    downloadJobArtifacts: wrapIfPresent('downloadJobArtifacts'),
  };
}
